import React, { ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf/dist/esm/entry.webpack';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import HTMLFlipBook, { PageFlip } from 'react-pageflip';
import Loader from 'react-loader-spinner';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearchPlus } from '@fortawesome/free-solid-svg-icons/faSearchPlus';
import { faSearchMinus } from '@fortawesome/free-solid-svg-icons/faSearchMinus';
import { faFastBackward } from '@fortawesome/free-solid-svg-icons/faFastBackward';
import { faFastForward } from '@fortawesome/free-solid-svg-icons/faFastForward';
import { faCaretLeft } from '@fortawesome/free-solid-svg-icons/faCaretLeft';
import { faCaretRight } from '@fortawesome/free-solid-svg-icons/faCaretRight';

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;

interface PageProps {
    number: number;
    width: number;
    height: number;
    loading: any;
    scale: number;
}

const FlipPage = React.forwardRef(
    (props: PageProps, ref) => (
        <div
            // @ts-ignore
            ref={ref}
            className='page-restrictor'
            style={{
                width: props.width,
                height: props.height,
            }}
        >
            <Page
                pageNumber={props.number + 1}
                width={props.width}
                height={props.height}
                loading={props.loading}
            />
        </div>
    )
);

interface Props {
    doc: string;
}

enum Orientation {
    Landscape = 1,
    Portrait = 2,
}

export default function Magazine(
    {
        doc,
    }: Props
): ReactElement {
    const [numPages, setNumPages] = useState<number | null>(null);

    const onDocumentLoadSuccess = useCallback((proxy: pdfjs.PDFDocumentProxy) => {
        setNumPages(proxy.numPages);
    }, []);

    const [width, setWidth] = useState<number | null>(null);
    const [height, setHeight] = useState<number | null>(null);
    const [orientation, setOrientation] = useState<Orientation | null>(null);
    const onPageLoadSuccess = useCallback((page: pdfjs.PDFPageProxy) => {
        const orientation = window.innerWidth > window.innerHeight ? Orientation.Landscape : Orientation.Portrait;
        setOrientation(orientation);

        const { height, width } = page.getViewport({
            scale: 1,
        });

        const scaleFactor = orientation === Orientation.Landscape
                            ? window.innerHeight / height * 1.1
                            : window.innerWidth / width * 1.25;

        setHeight(
            height * scaleFactor
        );
        setWidth(
            width * scaleFactor
        );
    }, []);

    const loadingContainer = useMemo(() => {
        return (
            <div className='magazine-loading'>
                <Loader
                    type='Oval'
                    color='#d6c6ad'
                />
                <strong>Fetching magazine...</strong>
                (this may take a moment)
            </div>
        );
    }, []);

    const [zoom, setZoom] = useState(1);
    const zoomIn = useCallback(() => {
        if (zoom < 8) {
            setZoom(zoom * 2);
        }
    }, [zoom]);

    const zoomOut = useCallback(() => {
        if (zoom > 1) {
            setZoom(zoom / 2);
        }
    }, [zoom]);

    const pages = useMemo(() => {
        return (
            Array(numPages).fill(0).map((e, i) =>
                <FlipPage
                    number={i}
                    key={i}
                    width={((width || 0) / 2) * zoom}
                    height={((height || 0) / 2) * zoom}
                    loading=''
                    scale={zoom}
                />
            )
        );
    }, [zoom, width, height]);

    const flipbook = useRef<null | PageFlip>(null);
    const [page, setPage] = useState(0);
    useEffect(() => {
        flipbook.current?.getPageFlip().turnToPage(page);
    }, [flipbook.current, zoom]);

    const handleFlip = useCallback((event: { data: number, object: PageFlip }) => {
        setPage(event.data);
    }, []);

    const toStart = useCallback(() => {
        flipbook.current?.getPageFlip().flip(0, 'top');
    }, [flipbook.current]);

    const toEnd = useCallback(() => {
        flipbook.current?.getPageFlip().flip(flipbook.current?.getPageFlip().getPageCount() - 1, 'top');
    }, [flipbook.current]);

    const forward = useCallback(() => {
        flipbook.current?.getPageFlip().flipNext('top');
    }, [flipbook.current]);

    const back = useCallback(() => {
        flipbook.current?.getPageFlip().flipPrev('top');
    }, [flipbook.current]);

    return (
        <>
            {
                numPages !== null && width !== null && height !== null && orientation !== null ? (
                    <Document
                        file={doc}
                        options={{
                            cMapUrl: `//cdn.jsdelivr.net/npm/pdfjs-dist@${pdfjs.version}/cmaps/`,
                            cMapPacked: true,
                        }}
                        renderMode='canvas'
                        loading={loadingContainer}
                    >
                        <div
                            className='options-wrapper'
                            style={{
                                width: width / orientation,
                                height: height / 2,
                            }}
                        >
                            <div
                                className='scaler'
                                style={{
                                    width: width / orientation,
                                    height: height / 2,
                                }}
                            >
                                <div
                                    className='restrictor'
                                    style={{
                                        width: width / orientation * zoom,
                                        height: (height / 2) * zoom,
                                        // left: orientation === Orientation.Portrait ? '-100%' : 0,
                                    }}
                                >
                                    <HTMLFlipBook
                                        ref={(component) => (flipbook.current = component)}
                                        width={(width / 2) * zoom}
                                        height={(height / 2) * zoom}
                                        maxShadowOpacity={0.3}
                                        usePortrait={orientation === Orientation.Portrait}
                                        showCover
                                        onFlip={handleFlip}
                                        children={pages}
                                        mobileScrollSupport
                                        useMouseEvents={zoom === 1}
                                    />
                                </div>
                            </div>
                            <div className={`controls${zoom === 1 ? ' hidden' : ''}`}>
                                <button
                                    onClick={back}
                                    className='control back-button'
                                    style={{
                                        left: 20,
                                    }}
                                >
                                    <FontAwesomeIcon icon={faCaretLeft} />
                                </button>
                                <button
                                    onClick={forward}
                                    className='control forward-button'
                                    style={{
                                        left: width / orientation - 50,
                                    }}
                                >
                                    <FontAwesomeIcon icon={faCaretRight} />
                                </button>
                            </div>
                            <div className='viewer-options'>
                                <button
                                    className='to-start'
                                    onClick={toStart}
                                >
                                    <FontAwesomeIcon icon={faFastBackward} />
                                </button>
                                <button
                                    className='zoom-out'
                                    onClick={zoomOut}
                                >
                                    <FontAwesomeIcon icon={faSearchMinus} />
                                </button>
                                <button
                                    className='zoom-in'
                                    onClick={zoomIn}
                                >
                                    <FontAwesomeIcon icon={faSearchPlus} />
                                </button>
                                <button
                                    className='to-end'
                                    onClick={toEnd}
                                >
                                    <FontAwesomeIcon icon={faFastForward} />
                                </button>
                            </div>
                        </div>
                    </Document>
                ) : (
                    <Document
                        file={doc}
                        options={{
                            cMapUrl: `//cdn.jsdelivr.net/npm/pdfjs-dist@${pdfjs.version}/cmaps/`,
                            cMapPacked: true,
                        }}
                        onLoadSuccess={onDocumentLoadSuccess}
                        loading={loadingContainer}
                    >
                        <Page
                            renderMode='none'
                            pageNumber={1}
                            onLoadSuccess={onPageLoadSuccess}
                            loading={loadingContainer}
                        />
                    </Document>
                )
            }
        </>
    );
}
